<template>
  <LayoutCard
    title="Administrative Information"
    description="This information is used in violation letters and emails to applicants and licensees."
    class="mb-4">

    <template #footer v-if="changed">
      <spinner-view v-if="saving" small class="me-3" />
      <button type="button" class="btn btn-flat ms-1" @click="reset" :disabled="saving">
        Cancel
      </button>
      <button type="button" class="btn btn-primary ms-1" @click="commit" :disabled="saving || invalidReplyToEmail">
        Save Changes
      </button>
    </template>

    <div v-if="saveError" class="alert alert-danger" role="alert">{{ saveError }}</div>

    <div class="d-flex fled-row">
      <div class="flex-grow-1 flex-column d-flex">

        <div class="row mb-3">
          <label class="col-sm-12 col-md-2 col-form-label">Website</label>
          <div class="col">
            <input type="text" v-model="admin.url" class="form-control"/>
          </div>
        </div>

        <div class="row mb-3 d-flex flex-row align-items-center">
          <label class="col-sm-12 col-md-2 col-form-label">Seal</label>
          <div class="col flex-grow-1 col-auto">
            <input type="text" v-model="admin.sealUrl" class="form-control"/>
          </div>
          <img v-if="admin.sealUrl" :src="admin.sealUrl" class="rounded overflow-hidden" style="object-fit: contain;max-width: 100px"/>
        </div>

        <div class="row mb-3">
          <label class="col-sm-12 col-md-2 col-form-label">Formal Town/City Name</label>
          <div class="col">
            <input type="text" v-model="admin.formalName" class="form-control"/>
            <div class="form-text">
              e.g. Town of {{ city.name }}
            </div>
          </div>
        </div>

        <div class="row mb-3">
          <label class="col-sm-12 col-md-2 col-form-label">Department Title</label>
          <div class="col">
            <input type="text" class="form-control" v-model="admin.officeTitle" />
            <div class="form-text">
              e.g. Office of the Town Clerk
            </div>
          </div>
        </div>

        <div class="row mb-3">
          <label class="col-sm-12 col-md-2 col-form-label">Department Address</label>
          <div class="col">
            <input type="text" v-model="admin.address" class="form-control"/>
          </div>
        </div>

        <div class="row mb-3">
          <label class="col-sm-12 col-md-2 col-form-label">Contact Person's Title</label>
          <div class="col">
            <input type="text" v-model="admin.contactTitle" class="form-control"/>
            <div class="form-text">
              e.g. {{ city.name }} Town Clerk
            </div>
          </div>
        </div>

        <div class="row mb-3">
          <label class="col-sm-12 col-md-2 col-form-label">Contact Person's Name</label>
          <div class="col">
            <input type="text" v-model="admin.contactName" class="form-control"/>
          </div>
        </div>

        <div class="row mb-3">
          <label class="col-sm-12 col-md-2 col-form-label">Contact Person's Phone Number</label>
          <div class="col">
            <PhoneInput v-model="admin.contactPhone" class="form-control"/>
          </div>
        </div>

        <div class="row mb-3">
          <label class="col-sm-12 col-md-2 col-form-label">Contact Person's Email</label>
          <div class="col">
            <input type="email" v-model="admin.contactEmail" class="form-control"/>
          </div>
        </div>

        <div class="row mb-3">
          <label for="staff-reply-to-email" class="col-sm-12 col-md-2 col-form-label">Staff reply-to email</label>
          <div class="col">
            <input
              id="staff-reply-to-email"
              type="email"
              v-model="admin.staffReplyToEmail"
              class="form-control"
              :class="{ 'is-invalid': invalidReplyToEmail }"
              :aria-invalid="invalidReplyToEmail"
              aria-describedby="staff-reply-to-help staff-reply-to-error"
              maxlength="254"
              :disabled="saving"
            />
            <div id="staff-reply-to-help" class="form-text">
              Replies to application confirmations, approval notices, and license status emails go to this address.
              Leave blank to use the sender's address.
            </div>
            <div v-if="invalidReplyToEmail" id="staff-reply-to-error" class="invalid-feedback">
              Enter one valid email address, or leave it blank.
            </div>
          </div>
        </div>

        <div class="row mb-3">
          <label class="col-sm-12 col-md-2 col-form-label">Violation Letter Title</label>
          <div class="col">
            <input type="text" class="form-control" v-model="admin.violationLetterTitle" />
            <div class="form-text">
              e.g. Notice of Potential Violation
            </div>
          </div>
        </div>

        <div class="row mb-3">
          <label class="col-sm-12 col-md-2 col-form-label">Violation Letter Subtitle</label>
          <div class="col">
            <input type="text" class="form-control" v-model="admin.violationLetterSubtitle" />
            <div class="form-text">
              e.g. Short-Term Rental Ordinance
            </div>
          </div>
        </div>
      </div>

    </div>
  </LayoutCard>
</template>

<script>
import {City} from "@/models/City";
import LayoutCard from "@/components/common/LayoutCard.vue";
import SpinnerView from "@/components/common/SpinnerView.vue";
import PhoneInput from "@/components/common/PhoneInput.vue";
import {normalizeStaffReplyToEmail} from "@strenforcement/domain/city";

export default {
  components: {PhoneInput, SpinnerView, LayoutCard},
  props: {
    city: City
  },
  data() {
    return {
      admin: {
        url: null,
        sealUrl: null,
        formalName: '',
        officeTitle: '',
        address: '',
        contactTitle: '',
        contactName: '',
        contactEmail: '',
        staffReplyToEmail: '',
        contactPhone: '',
        violationLetterTitle: '',
        violationLetterSubtitle: '',
        clerk: null,
        ordinance: null,
      },
      saving: false,
      saveError: null
    };
  },
  watch: {
    city: {
      immediate: true,
      handler() {
        this.reset();
      }
    }
  },
  computed: {
    /** Allows an empty setting but prevents saving malformed or multiple addresses. */
    invalidReplyToEmail() {
      const email = this.admin.staffReplyToEmail || '';
      return Boolean(email.trim()) && !normalizeStaffReplyToEmail(email);
    },

    changed() {
      let changed = false;
      Object.keys(this.admin).forEach(key => {
        if (this.admin[key] !== this.city.admin[key]) {
          changed = true;
        }
      });
      return changed;
    }
  },
  methods: {
    /** Restores the saved values, including the empty default for older municipalities. */
    reset() {
      this.saveError = null;
      this.admin = {
        ...this.admin,
        ...this.city.admin,
        staffReplyToEmail: this.city.admin.staffReplyToEmail || ''
      };
    },

    /** Persists valid settings and keeps edits available if saving fails. */
    async commit() {
      if (this.invalidReplyToEmail || this.saving) {
        return;
      }

      this.saving = true;
      this.saveError = null;

      try {
        await this.city.updateAdmin({
          ...this.admin,
          staffReplyToEmail: normalizeStaffReplyToEmail(this.admin.staffReplyToEmail) || ''
        });
        this.reset();
      } catch (error) {
        console.error('Could not save administrative information', error);
        this.saveError = 'Could not save changes. Please try again.';
      } finally {
        this.saving = false;
      }
    }
  }
}
</script>
